import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Badge from '@mui/material/Badge';
import { MainMenuItem } from '../../types/dashboard';
import styles from '../../layouts/dashboard-layouts/style/Sidebar.module.scss';
import { MenuItemWarningIcon } from './MenuItemWarningIcon/MenuItemWarningIcon';
import { usePushContext } from '../../context/PushNotificationContext';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { getBadgeNotificationStyles } from './constant';

type MenuItemProps = {
    item: MainMenuItem;
    isSidebarOpen: boolean;
    onClick: () => void;
    isDisabled?: boolean; 
    showWarning?: boolean;
}

export const MenuItem = ({ item, isSidebarOpen, onClick, isDisabled = false, showWarning = false }: MenuItemProps) => {
    const { t } = useTranslation();
    const { unreadCount } = usePushContext();
    const { isDirty, triggerReminder } = useUnsavedChanges();
    const location = useLocation();

    const isNotificationItem = item.path === '/notifications';

    // Calculate active state by checking both pathname and 'view' query param
    const currentSearchParams = new URLSearchParams(location.search);
    const itemUrl = new URL(item.path || '/', 'http://localhost');
    const itemSearchParams = new URLSearchParams(itemUrl.search);
    
    let isPathMatch = location.pathname === itemUrl.pathname;
    // Allow availability child routes to highlight the parent menu item
    if (itemUrl.pathname === '/availability' && location.pathname.startsWith('/availability')) {
        isPathMatch = true;
    }
    // Allow blog child routes (/blog, /blog/create, /blog/edit/:id) to highlight the Artículos menu item
    if (itemUrl.pathname === '/blog' && location.pathname.startsWith('/blog')) {
        isPathMatch = true;
    }
    // Allow reservations and consultations child routes to highlight their menu item
    if (itemUrl.pathname === '/reservations' && location.pathname.startsWith('/reservations')) {
        isPathMatch = true;
    }
    if (itemUrl.pathname === '/my-consultation' && location.pathname.startsWith('/my-consultation')) {
        isPathMatch = true;
    }
    
    const isViewMatch = currentSearchParams.get('view') === itemSearchParams.get('view');
    const isActive = isPathMatch && isViewMatch;

    const handleItemClick = (e: React.MouseEvent) => {
        if (isDisabled) {
            e.preventDefault();
            return;
        }
        if (isDirty) {
            e.preventDefault();
            triggerReminder();
            return;
        }
        onClick();
    };

    return (
        <NavLink
            to={item.path!}
            className={classNames(
                styles['sidebar__menu-item'],
                {
                    [styles['sidebar__menu-item--active']]: isActive,
                    [styles['sidebar__menu-item--disabled']]: isDisabled, 
                }
            )}
            onClick={handleItemClick}
            style={{ position: 'relative' }}
        >
            <div className={styles['sidebar__item-left']}>
                <div className={styles['sidebar__icon']}>
                    {item.icon}
                </div>
                {isSidebarOpen && <span className={styles['sidebar__label']}>{t(item.labelKey)}</span>}
            </div>

            {isNotificationItem && unreadCount > 0 && (
                <Badge 
                    badgeContent={unreadCount} 
                    max={99}
                    sx={getBadgeNotificationStyles(isSidebarOpen)}
                />
            )}

            {showWarning && <MenuItemWarningIcon className={styles['sidebar__alert-dot']} position="top" />}
        </NavLink>
    );
};